import {
  ComponentRef,
  DestroyRef,
  Directive,
  Type,
  ViewContainerRef,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';

type LegacyOutputs = Record<string, (value: any) => void>;

interface LegacyEmitter {
  subscribe(next: (value: unknown) => void): { unsubscribe(): void };
}

/**
 * Pont de transition : instancie un composant de l'ancien front, y compris `standalone: false`.
 *
 * Les composants déclarés dans AppModule ne peuvent pas figurer dans le tableau `imports`
 * d'un composant standalone. Comme l'app démarre en `bootstrapModule(AppModule)`, ils sont
 * pourtant déjà compilés avec leur scope : on les crée donc par ViewContainerRef, sans
 * toucher ni au composant ni à AppModule.
 *
 * Usage : <ng-container [appLegacyOutlet]="Type" [appLegacyOutletInputs]="{...}" [appLegacyOutletOutputs]="{...}" />
 */
@Directive({
  selector:   '[appLegacyOutlet]',
  standalone: true,
})
export class LegacyOutletDirective {
  readonly component = input.required<Type<unknown>>({ alias: 'appLegacyOutlet' });
  readonly inputs    = input<Record<string, unknown>>({}, { alias: 'appLegacyOutletInputs' });
  readonly outputs   = input<LegacyOutputs>({}, { alias: 'appLegacyOutletOutputs' });

  private readonly viewContainer = inject(ViewContainerRef);

  private componentRef: ComponentRef<unknown> | null = null;
  private subscriptions: { unsubscribe(): void }[] = [];

  constructor() {
    effect(() => {
      const type   = this.component();
      const inputs = this.inputs();
      untracked(() => {
        if (this.componentRef?.componentType !== type) {
          this.mount(type);
        }
        for (const [name, value] of Object.entries(inputs)) {
          this.componentRef!.setInput(name, value);
        }
      });
    });

    inject(DestroyRef).onDestroy(() => this.unmount());
  }

  /** Crée le composant, lui pose `w-100` comme dans tir-detail et branche ses sorties. */
  private mount(type: Type<unknown>): void {
    this.unmount();
    this.componentRef = this.viewContainer.createComponent(type);
    (this.componentRef.location.nativeElement as HTMLElement).classList.add('w-100');

    const instance = this.componentRef.instance as Record<string, LegacyEmitter | undefined>;
    for (const [name, handler] of Object.entries(this.outputs())) {
      const emitter = instance[name];
      if (emitter?.subscribe) {
        this.subscriptions.push(emitter.subscribe(handler));
      }
    }
  }

  private unmount(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
    this.componentRef?.destroy();
    this.componentRef = null;
  }
}
